import { NgModule, APP_INITIALIZER, isDevMode, ErrorHandler, CUSTOM_ELEMENTS_SCHEMA, inject } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { ServiceWorkerModule } from '@angular/service-worker';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { ToastModule } from 'primeng/toast';
import {
  provideKeycloak,
  withAutoRefreshToken,
  AutoRefreshTokenService,
  UserActivityService,
  includeBearerTokenInterceptor,
  createInterceptorCondition,
  INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
  IncludeBearerTokenCondition
} from 'keycloak-angular';
import Keycloak from 'keycloak-js';
import { environment } from '../environments/environment';
import { ApiModule, Configuration, ConfigurationParameters } from "@mobiletract/esg-calculator-service-angular-client";
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MessageService, ConfirmationService } from 'primeng/api';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { DateInterceptor } from './shared/interceptors/date-interceptor';
import { LanguageInterceptor } from './shared/interceptors/language-interceptor';
import { httpErrorInterceptor } from './shared/interceptors/http-error.interceptor';
import { SharedModule } from './shared/shared.module';
import { companyContextReducer, localStorageMetaReducer } from './core/state/company/company-context.reducer';
import { Router } from '@angular/router';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { CompanyContextEffects } from './core/state/company/company-context.effects';
import { DrawerModule } from 'primeng/drawer';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonModule } from 'primeng/button';
import { ChipModule } from 'primeng/chip';
import { MessageModule } from 'primeng/message';
import { AppPanelComponent } from "./shared/components/app-panel/app-panel.component";
import { ConferenceFormModule } from "./features/conference-form/conference-form.module";
import { SummaryEffects } from './core/state/summary/summary.effects';
import { UserEffects } from './core/state/user/user.effects';
import { summaryReducer } from './core/state/summary/summary.reducer';
import { version } from '../../package.json';

import { GoogleTagManagerModule } from 'angular-google-tag-manager';
import * as Sentry from '@sentry/angular';

import { QRCodeModule } from 'angularx-qrcode';
import { userReducer } from './core/state/user/user.reducer';
import { ZXingScannerModule } from '@zxing/ngx-scanner';
import { NgxEchartsModule } from 'ngx-echarts';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { providePrimeNG } from 'primeng/config';
import {
  ekowitrynaPreset,
  EkoLayoutHeaderComponent,
  EkoLayoutSidebarComponent,
  EkoLayoutFooterComponent,
  EkoDarkModeToggleComponent,
  EkoUserProfileComponent,
  EkoNavigationMenuComponent,
  EkoLanguageSelectorComponent,
  MultiTranslateHttpLoader,
  EkoLanguageService
} from '@ekowitryna/ui';
import { TranslateService } from '@ngx-translate/core';

export function HttpLoaderFactory(http: HttpClient) {
  return new MultiTranslateHttpLoader(http, [
    { prefix: './assets/i18n/', suffix: '.json?version=' + version },
    { prefix: './assets/ui/i18n/', suffix:  '.json?version=' + version },
  ]);
}

// Initialize translations on app startup
export function initializeTranslations(
  languageService: EkoLanguageService
) {
  return () => {
    // Get saved language or use default
    const savedLang = localStorage.getItem('appLang');
    const supportedLangs = ['pl', 'en', 'de'];
    const defaultLang = savedLang && supportedLangs.includes(savedLang) ? savedLang : 'pl';

    // Initialize language service with supported languages and default
    // This will load translations once at app startup
    languageService.initializeLanguage(supportedLangs, defaultLang);
  };
}

// Keycloak bearer token interceptor configuration
const urlCondition = createInterceptorCondition<IncludeBearerTokenCondition>({
  urlPattern: new RegExp(`^${environment.apiUrl.replace(/\/$/, '')}.*`),
  bearerPrefix: 'Bearer'
});

@NgModule({ declarations: [
        AppComponent
    ],
    bootstrap: [AppComponent], imports: [BrowserModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        AppPanelComponent,
        ToastModule,
        MessageModule,
        FormsModule,
        ReactiveFormsModule,
        ConferenceFormModule,
        SharedModule,
        DrawerModule,
        TooltipModule,
        ButtonModule,
        ChipModule,
        ZXingScannerModule,
        ConfirmPopupModule,
        EkoLayoutHeaderComponent,
        EkoLayoutSidebarComponent,
        EkoLayoutFooterComponent,
        EkoDarkModeToggleComponent,
        EkoUserProfileComponent,
        EkoNavigationMenuComponent,
        EkoLanguageSelectorComponent,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient]
            }
        }),
        StoreModule.forRoot({
            companyContext: companyContextReducer,
            summary: summaryReducer,
            user: userReducer
        }, {
            metaReducers: [
                localStorageMetaReducer,
            ]
        }),
        EffectsModule.forRoot([]),
        ApiModule,
        EffectsModule.forFeature([CompanyContextEffects, SummaryEffects, UserEffects]),
        StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: !isDevMode() }),
        GoogleTagManagerModule,
        QRCodeModule,
        NgxEchartsModule.forRoot({
            echarts: () => import('echarts')
        }),
        ServiceWorkerModule.register('ngsw-worker.js', {
            enabled: !isDevMode(),
            // Register the ServiceWorker as soon as the application is stable
            // or after 30 seconds (whichever comes first).
            registrationStrategy: 'registerWhenStable:30000'
        })], providers: [
        providePrimeNG({
            theme: {
                preset: ekowitrynaPreset,
                options: {
                    darkModeSelector: '.dark'
                }
            }
        }),
        MessageService,
        ConfirmationService,
        provideAnimationsAsync(),
        { provide: 'googleTagManagerId', useValue: environment.googleTagManagerId },
        { provide: 'googleTagManagerPreview', useValue: environment.googleTagManagerPreview },
        { provide: 'googleTagManagerAuth', useValue: environment.googleTagManagerAuth },
        {
          provide: Configuration,
          useFactory: esgApiConfigFactory
        },
        { provide: HTTP_INTERCEPTORS, useClass: DateInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: LanguageInterceptor, multi: true },
        provideKeycloak({
            config: {
                url: environment.keycloakUrl,
                realm: environment.realm,
                clientId: environment.clientId
            },
            initOptions: {
                onLoad: 'login-required',
                checkLoginIframe: false
            },
            providers: [
                UserActivityService,
                AutoRefreshTokenService
            ],
            features: [
                withAutoRefreshToken({
                  onInactivityTimeout: 'login'
                })
            ]
        }),
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler({
                showDialog: false,
            }),
        }, {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        {
            provide: APP_INITIALIZER,
            useFactory: () => () => { },
            deps: [Sentry.TraceService],
            multi: true,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initializeTranslations,
            deps: [EkoLanguageService],
            multi: true,
        },
        {
          provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
          useValue: [urlCondition]
        },
        provideHttpClient(
          withInterceptors([includeBearerTokenInterceptor, httpErrorInterceptor]),
          withInterceptorsFromDi()
        )
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA] })
export class AppModule { }

export function esgApiConfigFactory (): Configuration {
  const keycloak = inject(Keycloak);
  const params: ConfigurationParameters = {
    basePath: `${environment.apiUrl}esg`,
    credentials: {
      'bearer': () => {
        const token = keycloak?.token;
        return token || undefined;
      }
    }
  };
  return new Configuration(params);
}
